import type { CarSpec, Gear, GearDenyReason } from './types';

/**
 * Modelo de transmisión automática + motor. Módulo puro y determinista:
 * no toca la física, solo calcula fuerzas a partir del estado.
 */

export const IDLE_RPM = 750;
export const REDLINE_RPM = 6300;
export const REVERSE_RATIO = 3.1;
/** Velocidad máxima (m/s) a la que el convertidor sigue "arrastrando" el coche. */
export const CREEP_MAX_MS = 2.4;

export interface DrivetrainState {
  gear: Gear;
  ratioIndex: number;
  rpm: number;
  shiftTimerS: number;
  shiftCooldownS: number;
}

export function createDrivetrain(): DrivetrainState {
  return { gear: 'P', ratioIndex: 0, rpm: IDLE_RPM, shiftTimerS: 0, shiftCooldownS: 0 };
}

export interface DrivetrainInput {
  throttle: number;
  brake: number;
  /** Velocidad longitudinal con signo (m/s, + adelante). */
  speedMs: number;
  /** Límite de tracción del eje motriz (N) para el control de tracción. */
  gripCapN: number;
  dt: number;
}

export interface DrivetrainOutput {
  /** Fuerza motriz en las ruedas (N, con signo: + adelante). Incluye creep. */
  driveForceN: number;
  /** Magnitud de retención de motor / regenerativa (N), aplicar oponiéndose al avance. */
  engineBrakeN: number;
  rpm: number;
  shifting: boolean;
  tcActive: boolean;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Curva de par normalizada (0..1) en función de las rpm. */
export function normTorque(rpm: number): number {
  if (rpm < 1000) return 0.55 + (0.15 * (rpm - IDLE_RPM)) / 250;
  if (rpm < 3800) return 0.7 + (0.3 * (rpm - 1000)) / 2800;
  if (rpm < 5100) return 1.0;
  return Math.max(0.55, 1.0 - (0.45 * (rpm - 5100)) / (REDLINE_RPM - 5100));
}

function currentRatio(st: DrivetrainState, spec: CarSpec): number {
  if (st.gear === 'R') return REVERSE_RATIO * spec.finalDrive;
  const g = spec.gearRatios[clamp(st.ratioIndex, 0, spec.gearRatios.length - 1)] ?? 1;
  return g * spec.finalDrive;
}

function mechRpmFor(spec: CarSpec, ratio: number, drivenSpeedMs: number): number {
  const wheelRps = Math.max(0, drivenSpeedMs) / (2 * Math.PI * spec.dims.wheelRadiusM);
  return wheelRps * 60 * ratio;
}

/**
 * Valida un cambio de selector solicitado. Devuelve null si es válido
 * o el motivo del rechazo (bloqueos lógicos de un automático real).
 */
export function validateGearChange(
  from: Gear,
  to: Gear,
  speedMs: number,
  brake: number,
): GearDenyReason | null {
  if (from === to) return null;
  // Las marchas numéricas pertenecen a la caja manual: un selector automático
  // las ignora (el InputManager ya las filtra por modo).
  if (typeof to === 'number' || typeof from === 'number') return 'moving';
  if (from === 'P' && brake < 0.2) return 'brakeRequired'; // pisar freno para salir de P
  switch (to) {
    case 'P':
      if (Math.abs(speedMs) > 0.7) return 'moving';
      return null;
    case 'R':
      if (speedMs > 0.8) return 'moving'; // nunca R avanzando
      return null;
    case 'D':
      if (speedMs < -0.8) return 'moving'; // nunca D retrocediendo
      return null;
    case 'N':
      return null;
  }
}

/** Aplica un cambio de selector ya validado. */
export function applyGearChange(st: DrivetrainState, to: Gear): void {
  st.gear = to;
  st.ratioIndex = 0;
  st.shiftTimerS = 0.2;
  st.shiftCooldownS = 0.4;
}

export function stepDrivetrain(
  st: DrivetrainState,
  spec: CarSpec,
  inp: DrivetrainInput,
): DrivetrainOutput {
  const { dt } = inp;
  st.shiftTimerS = Math.max(0, st.shiftTimerS - dt);
  st.shiftCooldownS = Math.max(0, st.shiftCooldownS - dt);
  const isEv = spec.regenBraking === true;
  const maxIdx = spec.gearRatios.length - 1;

  let targetRpm = IDLE_RPM;
  let drive = 0;
  let engineBrake = 0;
  let tcActive = false;

  if (st.gear === 'D' || st.gear === 'R') {
    const dir = st.gear === 'D' ? 1 : -1;
    const drivenSpeed = inp.speedMs * dir; // velocidad en el sentido de la marcha
    const ratio = currentRatio(st, spec);
    const mech = mechRpmFor(spec, ratio, drivenSpeed);

    // Programa de cambios (solo en D con caja multi-relación)
    if (st.gear === 'D' && maxIdx > 0 && st.shiftCooldownS === 0 && st.shiftTimerS === 0) {
      const upPoint = 2000 + inp.throttle * 3600;
      const downPoint = 1150 + inp.throttle * 500;
      if (mech > upPoint && st.ratioIndex < maxIdx) {
        st.ratioIndex += 1;
        st.shiftTimerS = 0.28;
        st.shiftCooldownS = 0.85;
      } else if (mech < downPoint && st.ratioIndex > 0) {
        st.ratioIndex -= 1;
        st.shiftTimerS = 0.22;
        st.shiftCooldownS = 0.6;
      } else if (inp.throttle > 0.85 && st.ratioIndex > 0) {
        // Kickdown: reducir mientras la marcha inferior no pase de vueltas
        const lowerRatio =
          (spec.gearRatios[st.ratioIndex - 1] ?? 1) * spec.finalDrive;
        if (mechRpmFor(spec, lowerRatio, drivenSpeed) < REDLINE_RPM * 0.92) {
          st.ratioIndex -= 1;
          st.shiftTimerS = 0.24;
          st.shiftCooldownS = 0.35; // permite bajar varias marchas seguidas
        }
      }
    }

    const ratioNow = currentRatio(st, spec);
    const mechNow = mechRpmFor(spec, ratioNow, drivenSpeed);
    // Convertidor de par: al arrancar, el motor gira por encima de la transmisión
    const converterSlip = clamp(1 - mechNow / 2400, 0, 1);
    if (isEv) {
      targetRpm = clamp(mechNow, 0, REDLINE_RPM); // "rpm" del motor eléctrico ≈ ruedas
    } else {
      targetRpm = clamp(
        Math.max(mechNow, IDLE_RPM + inp.throttle * 2300 * converterSlip),
        IDLE_RPM,
        REDLINE_RPM,
      );
    }

    // Par disponible (curva plana e instantánea para EV)
    const shifting = st.shiftTimerS > 0;
    const curveTq = isEv
      ? spec.maxTorqueNm * clamp(1.15 - targetRpm / REDLINE_RPM, 0.35, 1)
      : spec.maxTorqueNm * normTorque(Math.max(targetRpm, IDLE_RPM));
    const powerCapTq =
      targetRpm > 100 ? (spec.maxPowerKw * 9549) / targetRpm : Number.POSITIVE_INFINITY;
    const engTq = Math.min(curveTq, powerCapTq);
    const converterMult = isEv ? 1 : 1 + 0.85 * converterSlip;
    const shiftCut = shifting ? 0.3 : 1;
    drive =
      ((engTq * inp.throttle * ratioNow * converterMult * 0.9) /
        spec.dims.wheelRadiusM) *
      shiftCut *
      dir;

    // Creep: el automático avanza solo al soltar el freno
    if (inp.throttle < 0.06 && drivenSpeed < CREEP_MAX_MS) {
      const creep = spec.creepForceN * clamp(1 - drivenSpeed / CREEP_MAX_MS, 0, 1);
      drive += creep * dir;
    }

    // Retención de motor / regenerativa al levantar el pie
    if (inp.throttle < 0.04 && drivenSpeed > 1.2) {
      const coef = isEv ? 0.34 : 0.055;
      engineBrake = (coef * spec.maxTorqueNm * ratioNow) / spec.dims.wheelRadiusM;
    }

    // Control de tracción: recorta la fuerza al límite de agarre del eje
    if (Math.abs(drive) > inp.gripCapN) {
      drive = Math.sign(drive) * inp.gripCapN;
      if (spec.assists.tc && inp.throttle > 0.25) tcActive = true;
    }
  } else {
    // P o N: el motor revoluciona libre
    targetRpm = isEv
      ? 0
      : clamp(IDLE_RPM + inp.throttle * (REDLINE_RPM - 900 - IDLE_RPM), IDLE_RPM, REDLINE_RPM - 300);
  }

  // Inercia del motor (subida más rápida que bajada)
  const rate = targetRpm > st.rpm ? 5200 : 3200;
  st.rpm += clamp(targetRpm - st.rpm, -rate * dt, rate * dt);

  return {
    driveForceN: drive,
    engineBrakeN: engineBrake,
    rpm: st.rpm,
    shifting: st.shiftTimerS > 0,
    tcActive,
  };
}
